import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  getAskDynamoSession,
  getSampleThoughts,
  postAskDynamoMessage,
  AskDynamoApiError,
  type AskDynamoMessage,
  type AskDynamoSessionResponse,
  type SampleThought,
} from "~/api/askDynamo"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { activateDog } from "~/api/dogs"
import {
  fetchAskStorySavedStatus,
  saveAskConversationToStory,
} from "~/api/story"
import { AskDynamoComposer } from "~/components/AskDynamoComposer"
import { AskDynamoFollowUpSuggestions } from "~/components/AskDynamoFollowUpSuggestions"
import { AskDynamoHeader } from "~/components/AskDynamoHeader"
import { AskDynamoJumpToLatestButton } from "~/components/AskDynamoJumpToLatestButton"
import { AskDynamoMessageList } from "~/components/AskDynamoMessageList"
import { AskDynamoPawIllustration } from "~/components/AskDynamoPawIllustration"
import { AskDynamoSuggestionList } from "~/components/AskDynamoSuggestionList"
import { AskDynamoThoughtsPreferenceCard } from "~/components/AskDynamoThoughtsPreferenceCard"
import { DogSelector } from "~/components/DogSelector"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { SafetyDisclaimer } from "~/components/SafetyDisclaimer"
import { useAtBottom } from "~/hooks/useAtBottom"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"
import { trackEvent } from "~/utils/trackEvent"
import { storeLoginReturnPath } from "~/utils/loginReturnPath"

function formatPackNames(names: string[]): string {
  if (names.length === 0) return "your dog"
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}

export function AskDynamoPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [session, setSession] = useState<AskDynamoSessionResponse | null>(null)
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [messages, setMessages] = useState<AskDynamoMessage[]>([])
  const [composerValue, setComposerValue] = useState("")
  const [followUpQuestions, setFollowUpQuestions] = useState<SampleThought[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [storyNote, setStoryNote] = useState("")
  const [storyMessage, setStoryMessage] = useState<string | null>(null)
  const [savingStory, setSavingStory] = useState(false)
  const [storyAlreadySaved, setStoryAlreadySaved] = useState(false)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
  const [selectedDogIds, setSelectedDogIds] = useState<number[]>([])
  const [packMode, setPackMode] = useState(false)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const lastShownSequenceNumbers = useRef<number[]>([])
  const { bottomRef, isAtBottom, scrollToBottom } = useAtBottom()
  const wasAtBottomRef = useRef(true)
  const hydratedRef = useRef(false)

  useEffect(() => {
    Promise.all([getAskDynamoSession(), getProfile()])
      .then(([data, profileData]) => {
        setSession(data)
        setMessages(data.messages)
        setProfile(profileData)
        if (profileData.active_dog_profile_id) {
          setSelectedDogIds([profileData.active_dog_profile_id])
        }
        const prefill = searchParams.get("q")
        if (prefill) setComposerValue(prefill)
      })
      .catch(() => {
        const returnPath = `${location.pathname}${location.search}`
        storeLoginReturnPath(returnPath)
        navigate(`/login?return=${encodeURIComponent(returnPath)}`, { replace: true })
      })
      .finally(() => setLoading(false))
  }, [location.pathname, location.search, navigate, searchParams])

  useEffect(() => {
    if (!selectedDogIds.length) return
    getAskDynamoSession(selectedDogIds)
      .then((data) => {
        setSession(data)
        setMessages(data.messages)
      })
      .catch(() => undefined)
  }, [selectedDogIds])

  useEffect(() => {
    wasAtBottomRef.current = isAtBottom
  }, [isAtBottom])

  useEffect(() => {
    if (!loading && session && !hydratedRef.current) {
      hydratedRef.current = true
      wasAtBottomRef.current = isAtBottom
      return
    }
    if (!hydratedRef.current) return
    if (!wasAtBottomRef.current) return
    scrollToBottom("instant")
  }, [messages, sending, scrollToBottom, loading, session])

  const activeDogs = profile?.dogs ?? []
  const selectedDogs = activeDogs.filter((dog) => selectedDogIds.includes(dog.id))
  const contextNames = selectedDogs.map((dog) => dog.name)
  const allActiveDogsSelected =
    packMode &&
    selectedDogIds.length > 1 &&
    selectedDogIds.length === activeDogs.length &&
    activeDogs.length >= 2
  const packHeading =
    packMode && selectedDogIds.length > 1
      ? allActiveDogsSelected
        ? "your pack"
        : formatPackNames(contextNames)
      : (contextNames[0] ?? session?.dog.name ?? "your dog")
  const isMultiDogContext = packMode && selectedDogIds.length > 1
  const personalityPhrase = isMultiDogContext
    ? "their unique personalities"
    : `${packHeading}'s unique personality`
  const storyTargetLabel = isMultiDogContext
    ? allActiveDogsSelected
      ? "your pack"
      : formatPackNames(contextNames)
    : packHeading
  const dogName = packHeading
  const isUnlimited = session?.usage.daily_remaining == null
  const dailyRemaining = session?.usage.daily_remaining ?? 0
  const limitReached = !isUnlimited && dailyRemaining === 0
  const showLanding = messages.length === 0 && !sending
  const showFollowUp = messages.some((message) => message.role === "assistant") && !sending
  const hasJournal = hasEntitlement(profile?.user.entitlements, ENTITLEMENTS.dogJournal)
  const hasPackQuestions = hasEntitlement(profile?.user.entitlements, ENTITLEMENTS.packQuestions)
  const canUsePackMode = hasPackQuestions && activeDogs.length >= 2
  const lastAssistant = [...messages].reverse().find((message) => message.role === "assistant")
  const lastAssistantId = lastAssistant?.id
  const dogSelectorLabel = allActiveDogsSelected
    ? `Your Pack · ${activeDogs.length}`
    : undefined

  useEffect(() => {
    if (!hasJournal || !lastAssistantId || selectedDogIds.length === 0) {
      setStoryAlreadySaved(false)
      return
    }
    let cancelled = false
    fetchAskStorySavedStatus(lastAssistantId, selectedDogIds)
      .then((status) => {
        if (!cancelled) setStoryAlreadySaved(Boolean(status.all_selected_saved))
      })
      .catch(() => {
        if (!cancelled) setStoryAlreadySaved(false)
      })
    return () => {
      cancelled = true
    }
  }, [hasJournal, lastAssistantId, selectedDogIds])

  useEffect(() => {
    if (!showFollowUp) {
      setFollowUpQuestions([])
      return
    }

    let cancelled = false

    getSampleThoughts(3, lastShownSequenceNumbers.current, selectedDogIds)
      .then((thoughts) => {
        if (cancelled) return
        setFollowUpQuestions(thoughts)
        lastShownSequenceNumbers.current = thoughts.map((thought) => thought.sequence_number)
      })
      .catch(() => {
        if (!cancelled) setFollowUpQuestions([])
      })

    return () => {
      cancelled = true
    }
  }, [showFollowUp, messages, selectedDogIds])

  async function refreshSessionAndProfile(dogIds: number[] = selectedDogIds) {
    const [data, profileData] = await Promise.all([
      getAskDynamoSession(dogIds),
      getProfile(),
    ])
    setSession(data)
    setMessages(data.messages)
    setProfile(profileData)
    return profileData
  }

  async function handleSend(content: string) {
    const trimmed = content.trim()
    if (!trimmed || sending) return
    if (limitReached) {
      setShowUpgradePrompt(true)
      setError(null)
      return
    }

    setError(null)
    setShowUpgradePrompt(false)
    setSending(true)
    setComposerValue("")
    setStoryMessage(null)
    setStoryAlreadySaved(false)

    try {
      const result = await postAskDynamoMessage(trimmed, selectedDogIds)
      setMessages(result.messages)
      setSession((prev) =>
        prev ? { ...prev, usage: result.usage, messages: result.messages } : prev,
      )
      trackEvent("ask_dynamo_message_sent", { dogName })
    } catch (err) {
      if (err instanceof AskDynamoApiError && err.dailyLimitExceeded) {
        setSession((prev) =>
          prev
            ? {
                ...prev,
                usage: { ...prev.usage, daily_remaining: 0 },
              }
            : prev,
        )
        setShowUpgradePrompt(true)
        setError(null)
      } else {
        const message =
          err instanceof AskDynamoApiError
            ? err.message
            : "Could not send your question. Please try again."
        setError(message)
        setComposerValue(trimmed)
      }
    } finally {
      setSending(false)
    }
  }

  async function handleSaveToStory() {
    if (!hasJournal || !lastAssistant || storyAlreadySaved) return
    setSavingStory(true)
    setStoryMessage(null)
    const targets =
      selectedDogIds.length > 0
        ? selectedDogIds
        : ([profile?.active_dog_profile_id].filter(Boolean) as number[])
    try {
      const result = await saveAskConversationToStory({
        messageId: lastAssistant.id,
        dogIds: targets,
        userNote: storyNote.trim() || undefined,
      })
      setStoryNote("")
      setStoryAlreadySaved(result.already_saved || true)
      setStoryMessage(`✓ Added to ${storyTargetLabel}’s Story`)
    } catch (err) {
      setStoryMessage(err instanceof Error ? err.message : "Could not save to Story")
    } finally {
      setSavingStory(false)
    }
  }

  function handleSuggestionSelect(text: string) {
    if (limitReached) {
      setShowUpgradePrompt(true)
      return
    }
    setComposerValue(text)
  }

  function handleFollowUpSelect(text: string) {
    if (limitReached) {
      setShowUpgradePrompt(true)
      return
    }
    setComposerValue(text)
    composerRef.current?.focus()
  }

  function handleJumpToLatest() {
    scrollToBottom("smooth")
    trackEvent("ask_dynamo_jump_to_latest", { dogName })
  }

  async function toggleDogSelection(dogId: number) {
    if (!packMode) {
      setSelectedDogIds([dogId])
      if (dogId !== profile?.active_dog_profile_id) {
        try {
          await activateDog(dogId)
          await refreshSessionAndProfile([dogId])
        } catch {
          // Selection still drives Ask context via selectedDogIds.
        }
      }
      return
    }
    setSelectedDogIds((current) =>
      current.includes(dogId)
        ? current.length === 1
          ? current
          : current.filter((id) => id !== dogId)
        : [...current, dogId],
    )
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!session) return null

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <AskDynamoHeader />

        <div className="mt-4 flex justify-center">
          <DogSelector
            triggerLabel={dogSelectorLabel}
            onDogChanged={() => {
              void refreshSessionAndProfile().then((profileData) => {
                if (profileData.active_dog_profile_id) {
                  setSelectedDogIds([profileData.active_dog_profile_id])
                  setPackMode(false)
                }
              })
            }}
          />
        </div>

        {hasPackQuestions && activeDogs.length === 1 && (
          <section className="mt-4 rounded-xl border border-dashed border-[var(--color-ask-dynamo)]/30 bg-[var(--color-ask-dynamo-surface)]/30 p-4 text-center">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Pack Mode needs another dog
            </p>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              Add another dog to ask about your whole household together.
            </p>
            <Link
              to="/pack/add"
              className="mt-3 inline-block text-sm font-medium text-[var(--color-ask-dynamo)] underline-offset-2 hover:underline"
            >
              Add another dog
            </Link>
          </section>
        )}

        {canUsePackMode && (
          <section className="mt-4 rounded-xl border border-[var(--color-ask-dynamo)]/20 bg-[var(--color-ask-dynamo-surface)]/40 p-3">
            <p className="text-sm font-medium text-[var(--color-ask-dynamo-muted)]">
              Who is this question about?
            </p>
            <label className="mt-2 flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
              <input
                type="checkbox"
                checked={packMode}
                onChange={(e) => {
                  setPackMode(e.target.checked)
                  if (!e.target.checked && profile?.active_dog_profile_id) {
                    setSelectedDogIds([profile.active_dog_profile_id])
                  }
                }}
              />
              Pack Mode
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {activeDogs.map((dog) => (
                <button
                  key={dog.id}
                  type="button"
                  onClick={() => void toggleDogSelection(dog.id)}
                  aria-pressed={selectedDogIds.includes(dog.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    selectedDogIds.includes(dog.id)
                      ? "border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo)] text-white"
                      : "border-[var(--color-divider)]"
                  }`}
                >
                  {dog.name}
                </button>
              ))}
            </div>
          </section>
        )}

        {showLanding && (
          <section className="mt-6 text-center" aria-labelledby="ask-dynamo-hero-heading">
            <AskDynamoPawIllustration className="mx-auto h-20 w-20" />
            <h2
              id="ask-dynamo-hero-heading"
              className="mt-4 text-xl font-semibold text-[var(--color-text-primary)]"
            >
              What&apos;s on your mind about {packHeading} today?
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
              Ask a question about behaviour, training, or daily life — tailored to{" "}
              {personalityPhrase}.
            </p>
          </section>
        )}

        <div className="relative">
          <AskDynamoMessageList messages={messages} sending={sending} />
          <AskDynamoJumpToLatestButton
            visible={(messages.length > 0 || sending) && !isAtBottom}
            onClick={handleJumpToLatest}
          />
        </div>

        <div ref={bottomRef} aria-hidden className="h-px" />

        {lastAssistant && (
          <div className="mt-3 rounded-xl border border-[var(--color-ask-dynamo)]/15 bg-[var(--color-ask-dynamo-surface)]/50 p-3">
            {hasJournal ? (
              <>
                <label className="block text-sm text-[var(--color-text-primary)]">
                  Optional note for {storyTargetLabel}’s Story
                  <input
                    type="text"
                    value={storyNote}
                    onChange={(e) => setStoryNote(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-sm"
                    aria-label="Optional story note"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void handleSaveToStory()}
                  disabled={savingStory || storyAlreadySaved}
                  className="mt-2 text-sm font-medium text-[var(--color-ask-dynamo-muted)] underline disabled:opacity-50"
                >
                  {savingStory
                    ? "Saving…"
                    : storyAlreadySaved
                      ? `✓ Added to ${storyTargetLabel}’s Story`
                      : `♡ Save to ${storyTargetLabel}’s Story`}
                </button>
              </>
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Save conversations like this in {dogName}’s Story.{" "}
                <Link to="/plus" className="font-medium text-[var(--color-ask-dynamo-muted)] underline">
                  Available with Doggy Dynamo Plus →
                </Link>
              </p>
            )}
            {storyMessage && (
              <p className="mt-2 text-xs text-[var(--color-text-secondary)]" role="status">
                {storyMessage}
              </p>
            )}
          </div>
        )}

        <AskDynamoComposer
          ref={composerRef}
          dogName={dogName}
          value={composerValue}
          onChange={setComposerValue}
          onSubmit={() => void handleSend(composerValue)}
          sending={sending}
        />

        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {showUpgradePrompt && (
          <section
            className="mt-4 rounded-xl border border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo-surface)] p-4 text-center"
            role="status"
            aria-label="Free Ask Dynamo allowance exhausted"
          >
            <p className="text-base font-semibold text-[var(--color-text-primary)]">
              You’ve used today’s three free questions
            </p>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              Doggy Dynamo Plus unlocks unlimited Ask Dynamo conversations, Story, pack features, and
              weekly Perspectives.
            </p>
            <Link
              to="/plus"
              className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-3 text-sm font-semibold text-white hover:opacity-90"
            >
              Unlock unlimited questions with Plus
            </Link>
          </section>
        )}

        {showFollowUp && (
          <AskDynamoFollowUpSuggestions
            questions={followUpQuestions.map((thought) => thought.text)}
            onSelect={handleFollowUpSelect}
            disabled={sending}
          />
        )}

        {!isUnlimited && !showUpgradePrompt && (
          <section
            className="mt-4 rounded-xl border border-[var(--color-ask-dynamo)]/25 bg-[var(--color-ask-dynamo-surface)]/60 p-4 text-center"
            aria-label="Daily question allowance"
          >
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
              <strong className="text-[var(--color-ask-dynamo)]">{dailyRemaining}</strong> free
              question{dailyRemaining === 1 ? "" : "s"} remaining today
            </p>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              Unlimited Ask Dynamo conversations are included with Doggy Dynamo Plus.
            </p>
            <Link
              to="/plus"
              className="mt-2 inline-block text-sm font-medium text-[var(--color-ask-dynamo-muted)] underline"
            >
              Unlock unlimited questions →
            </Link>
          </section>
        )}

        {showLanding && session.thought_preference && (
          <AskDynamoThoughtsPreferenceCard
            initialFrequency={session.thought_preference.frequency}
            onboardingCompleted={!!session.thought_preference.onboarding_completed_at}
            onSaved={(frequency, onboardingCompleted) => {
              setSession((prev) =>
                prev && prev.thought_preference
                  ? {
                      ...prev,
                      thought_preference: {
                        ...prev.thought_preference,
                        frequency,
                        enabled: frequency !== "off",
                        onboarding_completed_at: onboardingCompleted
                          ? new Date().toISOString()
                          : prev.thought_preference.onboarding_completed_at,
                      },
                    }
                  : prev,
              )
            }}
          />
        )}

        {!hasJournal && (
          <p className="mt-2 text-center text-sm">
            <Link
              to="/plus"
              className="font-medium text-[var(--color-ask-dynamo-muted)] underline"
            >
              ✨ Discover Doggy Dynamo Plus →
            </Link>
          </p>
        )}

        {showLanding && (
          <AskDynamoSuggestionList
            dogName={dogName}
            suggestions={session.suggestions}
            onSelect={handleSuggestionSelect}
            disabled={sending}
            alreadyPersonalized
          />
        )}

        <SafetyDisclaimer />
      </LoggedInScreenCard>
    </PageShell>
  )
}
